/**
 * Proefperiode / facturatiestatus — o.a. Stripe self-serve voor tenant-beheerders.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, CreditCard, Sparkles } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

function daysLeft(iso: string | null): number | null {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const d = Math.ceil((end - Date.now()) / (24 * 60 * 60 * 1000));
  return d;
}

export function BillingBanner() {
  const { user, isAccountManager } = useAuth();
  const b = user?.tenant_billing;
  if (!user?.authenticated || !b) return null;

  const st = b.billing_status.toLowerCase();
  if (st === 'active') return null;

  if (st === 'suspended') {
    return (
      <div
        className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-100"
        role="alert"
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-start gap-2 sm:px-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            <strong>Account gedeactiveerd.</strong> Neem contact op met CalqTool om toegang te bespreken.{' '}
            <Link to="/contact" className="font-medium underline">
              Contact
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (st === 'invoice_pending' || st === 'past_due') {
    const pastDue = st === 'past_due';
    const title = pastDue ? 'Betaling achterstallig' : 'Afrekening nog niet afgerond';
    const adminLead = pastDue
      ? 'De laatste abonnementsbetaling is niet gelukt of er staat nog een openstaand bedrag. Los dit op via'
      : 'Je abonnement is nog niet volledig betaald of er staat een betaling open. Rond dit af via';
    const userLead = pastDue
      ? 'De laatste betaling voor jullie abonnement is mislukt of er staat nog iets open.'
      : 'Het betaalde abonnement voor jullie organisatie is nog niet volledig afgerekend.';

    return (
      <div
        className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-start gap-3 sm:px-2">
          <CreditCard className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="font-semibold text-text">{title}</p>
            {isAccountManager ? (
              <p className="leading-relaxed">
                {adminLead}{' '}
                <Link to="/account/billing" className="font-medium text-primary underline">
                  Facturatie
                </Link>{' '}
                (betaalmethode bijwerken, facturen en Stripe-klantportaal).{' '}
                {pastDue
                  ? 'Controleer je inbox op een e-mail van Stripe over de mislukte betaling.'
                  : 'Staat er een betaallink in je e-mail? Die kun je gebruiken, of ga direct naar Facturatie.'}
              </p>
            ) : (
              <p className="leading-relaxed">
                {userLead} Vraag de <strong className="text-text">accountbeheerder</strong> van jullie organisatie om in te
                loggen en onder <strong className="text-text">Account → Facturatie</strong> de betaling te voltooien of
                de betaalmethode te herstellen.
              </p>
            )}
            <p className="text-xs text-amber-900/85 dark:text-amber-100/90">
              Vragen of hulp nodig?{' '}
              <Link to="/contact" className="font-medium text-primary underline">
                Contact
              </Link>
              {' · '}
              <Link to="/prijzen" className="font-medium text-primary underline">
                Pakketten &amp; prijzen
              </Link>
              {isAccountManager ? (
                <>
                  {' · '}
                  <Link to="/status" className="font-medium text-primary underline">
                    Dienststatus
                  </Link>
                </>
              ) : null}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (st === 'trial') {
    const left = daysLeft(b.trial_ends_at);
    const exLeft = Math.max(0, b.limits.max_exports - b.usage_exports);
    const anLeft = Math.max(0, b.limits.max_analyses - b.usage_analyses);
    const projLeft = Math.max(0, b.limits.max_projects - b.project_count);
    return (
      <div
        className="border-b border-primary/25 bg-primary/[0.07] px-4 py-3 text-sm text-text dark:bg-primary/10"
        role="status"
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-start gap-2 sm:px-2">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <p>
            <strong>Proefperiode.</strong>{' '}
            {left != null && left >= 0 ? (
              <>
                Nog ca. <strong>{left}</strong> dag{left === 1 ? '' : 'en'} ·{' '}
              </>
            ) : null}
            exports <strong>{b.usage_exports}/{b.limits.max_exports}</strong>, tekeninganalyses{' '}
            <strong>
              {b.usage_analyses}/{b.limits.max_analyses}
            </strong>
            , dossiers <strong>{b.project_count}/{b.limits.max_projects}</strong>. Daarna heb je een betaald abonnement
            nodig.
            {isAccountManager ? (
              <>
                {' '}
                <Link to="/account/billing" className="font-medium text-primary underline">
                  Ga naar Facturatie
                </Link>{' '}
                om met Stripe af te sluiten, of{' '}
              </>
            ) : (
              ' Vraag je accountbeheerder om af te rekenen, of '
            )}
            <Link to="/contact" className="font-medium text-primary underline">
              neem contact op
            </Link>
            .
            {exLeft === 0 || anLeft === 0 || projLeft === 0 ? (
              <span className="mt-1 block font-medium text-amber-800 dark:text-amber-200">
                Een proeflimiet is bereikt; uitbreiding via factuur.
              </span>
            ) : null}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
